import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserStateEntry, UserStateMap } from '../types/user-state';
import { useUserState } from '../query/UserProvider';
import { useUpdateUserStateItem } from '../query/user-state/useUpdateUserStateItem';
import { useDeleteUserStateItem } from '../query/user-state/useDeleteUserStateItem';
import { isUserStateConflictError, applyUserStateEntry } from '../query/user-state/userStateOptimisticQuery';
import { userStateQueryKey } from '../query/user-state/userStateQueryKeys';
import { mergeConflictEntryField } from './tracked-job-conflict-merge';
import { buildSavedCompanyItemKey } from '../utils/user-state/saved-company-item-key';
import { normalizeUserStateVersion } from '../utils/user-state/normalize-user-state-version';
import type { WatchlistSeenSnapshot } from '../utils/user-state/watchlist-seen-snapshot';
import type { WatchlistScoreSeenSnapshot } from '../utils/user-state/watchlist-score-seen-snapshot';
import type { ThresholdAlertConfig } from '../utils/user-state/threshold-alert';

/**
 * Follow/unfollow a company for the Watchlist (SEEKER_HUB_PLAN.md Phase 4): a followed
 * company is a live-data subscription (open roles, employer score, trend), not a bookmark —
 * but its storage mechanics mirror useSavedJobActions.ts exactly (guest-first, synced to
 * UserState when authenticated). companyId PKs are case-sensitive — never lowercase.
 *
 * TEC-404 Phase C: the Redux `userState.entries` dual-write (`setEntry`/`removeEntry`) was
 * removed — `useFollowedCompanies.ts` (the only reader) already reads the TanStack cache, and
 * `updateUserStateItem`/`deleteUserStateItem`'s own optimistic-update lifecycle already gives
 * guests the same instant local feedback the manual Redux dispatch used to (see
 * `createUpdateUserStateMutationFn`'s guest branch: it echoes back whatever `onMutate` already
 * wrote into the cache, no network call). No manual `if (!isAuthenticated)` gate before writing —
 * those mutation hooks already branch on auth internally.
 *
 * This hook has no conflict-merge logic of its own beyond what already existed: `followCompany`/
 * `unfollowCompany` accept the server's winning entry wholesale on a 409 (nothing locally-typed to
 * protect), while `updateCompanyMeta` re-applies its patch on top of the server's entry — exactly
 * `tracked-job-conflict-merge.ts`'s `mergeConflictEntryField`, reused here rather than
 * reimplemented, since the original inline merge was byte-for-byte the same shape.
 */
export function useCompanyFollowActions() {
  const queryClient = useQueryClient();
  const { data: userState } = useUserState();
  const updateUserStateItem = useUpdateUserStateItem();
  const deleteUserStateItem = useDeleteUserStateItem();

  const followCompany = useCallback(
    async (companyId: string, extras: { companyName?: string } = {}): Promise<boolean> => {
      const itemKey = buildSavedCompanyItemKey(companyId);
      const followedAt = new Date().toISOString();
      const data = { companyId, companyName: extras.companyName ?? null, followedAt };

      const currentEntry = userState?.[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);
      try {
        await updateUserStateItem.mutateAsync({ itemKey, data, version });
        return true;
      } catch (error) {
        // useUpdateUserStateItem's own onError already reconciled the TanStack cache — on a 409
        // that means the server's winning entry (still followed), on any other failure that
        // means the pre-write entry was restored (rolled back to not-followed).
        return isUserStateConflictError(error);
      }
    },
    [updateUserStateItem, userState]
  );

  const unfollowCompany = useCallback(
    async (companyId: string): Promise<boolean> => {
      const itemKey = buildSavedCompanyItemKey(companyId);
      const previous = userState?.[itemKey] as UserStateEntry | undefined;

      try {
        await deleteUserStateItem.mutateAsync({ itemKey, version: previous?.version });
        return true;
      } catch {
        // useDeleteUserStateItem's own onError already restored the entry — either the previous
        // one, or the server's winning entry on a 409 (recreated concurrently). Either way it's
        // still followed, so the unfollow failed.
        return false;
      }
    },
    [deleteUserStateItem, userState]
  );

  /** Patches notes/watchlistSeen/watchlistScoreSeen/thresholdAlert onto an existing followed-company entry, preserving companyName/followedAt. */
  const updateCompanyMeta = useCallback(
    async (
      companyId: string,
      patch: {
        notes?: string;
        watchlistSeen?: WatchlistSeenSnapshot;
        watchlistScoreSeen?: WatchlistScoreSeenSnapshot;
        thresholdAlert?: ThresholdAlertConfig | null;
      }
    ): Promise<boolean> => {
      const itemKey = buildSavedCompanyItemKey(companyId);
      const currentEntry = userState?.[itemKey] as UserStateEntry | undefined;
      const currentData = (currentEntry?.data as Record<string, unknown> | undefined) ?? { companyId };
      const data = {
        ...currentData,
        companyId,
        ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
        ...(patch.watchlistSeen !== undefined ? { watchlistSeen: patch.watchlistSeen } : {}),
        ...(patch.watchlistScoreSeen !== undefined ? { watchlistScoreSeen: patch.watchlistScoreSeen } : {}),
        ...(patch.thresholdAlert !== undefined ? { thresholdAlert: patch.thresholdAlert } : {}),
      };

      const version = normalizeUserStateVersion(currentEntry?.version);
      try {
        await updateUserStateItem.mutateAsync({ itemKey, data, version });
        return true;
      } catch (error) {
        if (isUserStateConflictError(error)) {
          // Keep our just-typed edit locally even if the server had a newer version of the
          // rest of the entry — merge our patch on top rather than discarding it. The mutation's
          // own onError already wrote the server's raw entry into the TanStack cache — overwrite
          // it with the merged entry so the reader (useFollowedCompanies.ts) sees the
          // locally-kept field too.
          const merged = mergeConflictEntryField(error.data, data);
          queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
            applyUserStateEntry(current ?? {}, itemKey, merged)
          );
          return true;
        }
        return false;
      }
    },
    [queryClient, updateUserStateItem, userState]
  );

  return { followCompany, unfollowCompany, updateCompanyMeta };
}
